<script>
/**
 * @component OrdersCRM
 * @description UC-REG-162 — CRM de órdenes. Bandeja del operador comercial
 *              para trackear órdenes de lab/imagen/prácticas emitidas y
 *              ofrecer turnos a los pacientes.
 *
 *              Header con contadores (activas / turno tomado esta semana /
 *              cerradas). Filtros: rango de fechas (default 7 días), tipo,
 *              estado (default activos), prestador, cobertura, search.
 *              Modal detalle con acciones rápidas + historial.
 *
 * @example <OrdersCRM />
 */
import ordersCrmService from "@/services/orders-crm";
import { format, subDays, addDays } from "date-fns";
import { mapGetters } from "vuex";
import {
  getMedicalInsuranceName,
  getMedicalInsurancePlan,
  getMedicalInsuranceNumber,
} from "@/services/patient-utils";

const TYPE_META = {
  lab: { icon: "labs", label: "Lab" },
  images: { icon: "radiology", label: "Imagen" },
  practices: { icon: "assignment", label: "Práctica" },
};

const STATUS_META = {
  new: { label: "Nueva", cls: "is-new" },
  contacted: { label: "Contactado", cls: "is-contacted" },
  "appointment-proposed": { label: "Turno propuesto", cls: "is-proposed" },
  "appointment-taken": { label: "Turno tomado", cls: "is-taken" },
  closed: { label: "Cerrado", cls: "is-closed" },
};

export default {
  name: "OrdersCRM",

  data() {
    return {
      docs: [],
      total: 0,
      loading: false,
      exporting: false,
      searchDebounce: null,

      providers: [],
      coverages: [],
      counts: { active: 0, takenThisWeek: 0, closed: 0 },

      filters: {
        search: "",
        from: format(subDays(new Date(), 7), "yyyy-MM-dd"),
        to: format(addDays(new Date(), 7), "yyyy-MM-dd"),
        type: "",
        status: "active", // 'active' | 'appointment-taken' | 'closed' | ''
        doctor: "",
        coverage: "",
      },

      detail: {
        visible: false,
        indication: null,
        saving: false,
      },

      closeCase: {
        visible: false,
        note: "",
        saving: false,
      },

      // Modal de composición del WhatsApp comercial al paciente.
      // Se prearma con datos de la orden y el operador puede editar.
      whatsapp: {
        visible: false,
        message: "",
        sending: false,
      },
    };
  },

  computed: {
    ...mapGetters(["user"]),
  },

  created() {
    this.load();
    this.loadFilters();
  },

  watch: {
    "filters.from"() {
      this.reload();
    },
    "filters.to"() {
      this.reload();
    },
    "filters.type"() {
      this.reload();
    },
    "filters.status"() {
      this.reload();
    },
    "filters.doctor"() {
      this.reload();
    },
    "filters.coverage"() {
      this.reload();
    },
    "filters.search"() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.reload(), 350);
    },
  },

  methods: {
    /**
     * @description Trae la bandeja + counts. Se llama al montar y ante
     *              cualquier cambio de filtro.
     */
    async load() {
      this.loading = true;
      try {
        const params = this.buildParams();
        const [res, counts] = await Promise.all([
          ordersCrmService.list(params),
          ordersCrmService.counts({ from: params.from, to: params.to }),
        ]);
        this.docs = res.docs || [];
        this.total = res.total || 0;
        this.counts = counts || { active: 0, takenThisWeek: 0, closed: 0 };
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cargar el CRM"
        );
      } finally {
        this.loading = false;
      }
    },

    reload() {
      this.load();
    },

    async loadFilters() {
      try {
        const [providers, coverages] = await Promise.all([
          ordersCrmService.listProviders(),
          ordersCrmService.listCoverages(),
        ]);
        this.providers = providers || [];
        this.coverages = coverages || [];
      } catch (err) {
        // Non-fatal
      }
    },

    /**
     * @description Arma los params del GET /indication-crm a partir de
     *              los filtros del UI. Traduce el filtro 'active' del
     *              select a CSV de los 3 estados activos.
     */
    buildParams() {
      const p = {};
      if (this.filters.search && this.filters.search.trim().length >= 2) {
        p.search = this.filters.search.trim();
      }
      // Parseo LOCAL de YYYY-MM-DD — new Date("2026-07-30") lo interpreta
      // como UTC-midnight, que en ART (UTC-3) queda 21:00 del día anterior
      // y corta el rango 3 horas antes. Con Date(y, m-1, d) queda local.
      if (this.filters.from) {
        const [y, m, d] = this.filters.from.split("-").map(Number);
        p.from = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
      }
      if (this.filters.to) {
        const [y, m, d] = this.filters.to.split("-").map(Number);
        p.to = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
      }
      if (this.filters.type) p.type = this.filters.type;
      if (this.filters.status === "active") {
        p.status = "new,contacted,appointment-proposed";
      } else if (this.filters.status) {
        p.status = this.filters.status;
      }
      if (this.filters.doctor) p.doctor = this.filters.doctor;
      if (this.filters.coverage) p.coverage = this.filters.coverage;
      return p;
    },

    clearFilters() {
      this.filters = {
        search: "",
        from: format(subDays(new Date(), 7), "yyyy-MM-dd"),
        to: format(addDays(new Date(), 7), "yyyy-MM-dd"),
        type: "",
        status: "active",
        doctor: "",
        coverage: "",
      };
    },

    // ═══ Detalle ═══
    openDetail(row) {
      this.detail = { visible: true, indication: row, saving: false };
    },
    closeDetail() {
      this.detail.visible = false;
    },

    /**
     * @description Cambio de estado desde los botones de acción rápida.
     *              No confirma — el operador puede corregir con "Cerrar
     *              caso" o reasignando estado si se equivocó.
     */
    async changeStatus(status) {
      this.detail.saving = true;
      try {
        const updated = await ordersCrmService.updateStatus(
          this.detail.indication._id,
          status
        );
        this.detail.indication = updated;
        await this.load();
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo actualizar el estado"
        );
      } finally {
        this.detail.saving = false;
      }
    },

    // ═══ Exportar CSV ═══
    /**
     * @description Descarga CSV compatible con Excel de los resultados
     *              filtrados. Usa los mismos params que la bandeja.
     *              Excel abre bien BOM UTF-8 + delimitador `;`.
     */
    async exportCsv() {
      this.exporting = true;
      try {
        const params = this.buildParams();
        const blob = await ordersCrmService.exportCsv(params);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `crm-ordenes-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo generar el CSV"
        );
      } finally {
        this.exporting = false;
      }
    },

    // ═══ Contacto por WhatsApp (motor 2chat) ═══
    /**
     * @description Abre modal con mensaje prearmado. El operador edita
     *              y confirma. El backend transiciona el caso a
     *              'contacted' si estaba 'new'.
     */
    openWhatsapp() {
      const row = this.detail.indication;
      const patient = row && row.patient;
      if (!patient || !patient.phone) {
        this.$message.warning("El paciente no tiene teléfono cargado");
        return;
      }
      const nombre = patient.firstName
        ? patient.firstName.charAt(0).toUpperCase() +
          patient.firstName.slice(1).toLowerCase()
        : "";
      const estudio = this.orderTitle(row);
      const template =
        `Hola${nombre ? " " + nombre : ""}, te escribimos del centro médico. ` +
        `Vimos que tenés una orden de ${estudio} pendiente. ` +
        `¿Querés que te reservemos un turno? Podemos ofrecerte varios horarios. ` +
        `Gracias!`;
      this.whatsapp = { visible: true, message: template, sending: false };
    },
    cancelWhatsapp() {
      this.whatsapp.visible = false;
    },
    async sendWhatsapp() {
      const text = this.whatsapp.message.trim();
      if (!text) {
        this.$message.warning("Escribí un mensaje");
        return;
      }
      this.whatsapp.sending = true;
      try {
        const updated = await ordersCrmService.contactWhatsapp(
          this.detail.indication._id,
          text
        );
        this.detail.indication = updated;
        this.whatsapp.visible = false;
        this.$message.success("WhatsApp enviado");
        await this.load();
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo enviar el WhatsApp"
        );
      } finally {
        this.whatsapp.sending = false;
      }
    },

    // ═══ Cerrar caso (acción terminal, sí confirma con nota) ═══
    openCloseCase() {
      this.closeCase = { visible: true, note: "", saving: false };
    },
    cancelCloseCase() {
      this.closeCase.visible = false;
    },
    async confirmCloseCase() {
      this.closeCase.saving = true;
      try {
        const updated = await ordersCrmService.updateStatus(
          this.detail.indication._id,
          "closed",
          this.closeCase.note.trim() || undefined
        );
        this.detail.indication = updated;
        this.closeCase.visible = false;
        await this.load();
        this.$message.success("Caso cerrado");
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cerrar el caso"
        );
      } finally {
        this.closeCase.saving = false;
      }
    },

    // ═══ Helpers de render ═══
    formatDate(d) {
      if (!d) return "—";
      try {
        return format(new Date(d), "dd/MM HH:mm");
      } catch (e) {
        return "—";
      }
    },
    formatDateLong(d) {
      if (!d) return "—";
      try {
        return format(new Date(d), "dd/MM/yyyy HH:mm");
      } catch (e) {
        return "—";
      }
    },
    patientName(p) {
      if (!p) return "—";
      const parts = [p.firstName, p.lastName]
        .filter(Boolean)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
      return parts.join(" ") || p.email || "—";
    },
    patientDni(p) {
      return (p && p.governmentId && p.governmentId.number) || "";
    },
    doctorName(d) {
      if (!d) return "Dr eliminado";
      const parts = [d.firstName, d.lastName].filter(Boolean).join(" ");
      return parts || "—";
    },
    doctorSpecialty(d) {
      const first =
        d && d.doctor && d.doctor.specialties && d.doctor.specialties[0];
      if (!first) return "";
      return typeof first === "string" ? first : first.name || "";
    },
    doctorLicense(d) {
      return (d && d.doctor && d.doctor.license) || "";
    },

    /**
     * @description Etiqueta CORTA para la tabla de la bandeja. NO expande
     *              determinaciones — evita ensuciar la lista cuando una
     *              orden tiene muchas. Los detalles van en el modal.
     *              - lab       → title de la rutina si hay, sino "Lab"
     *              - images    → image.description + " · Imagen"
     *              - practices → title
     */
    orderShortLabel(row) {
      if (!row) return "";
      if (row.type === "lab") {
        return row.title || "Laboratorio";
      }
      if (row.type === "images") {
        return (row.image && row.image.description) || row.title || "Imagen";
      }
      if (row.type === "practices") {
        return row.title || row.description || "Práctica";
      }
      return row.title || "—";
    },
    /**
     * @description Título grande de la orden para el header del modal.
     *              Similar a orderShortLabel pero sin sufijo "· Imagen".
     */
    orderTitle(row) {
      if (!row) return "";
      if (row.type === "lab") return row.title || "Laboratorio";
      if (row.type === "images") {
        return (row.image && row.image.description) || row.title || "Imagen";
      }
      if (row.type === "practices") {
        return row.title || "Práctica";
      }
      return row.title || "—";
    },
    /**
     * @description Determinaciones/items de la orden para render prolijo
     *              en el modal (chips). Solo aplica a lab: devuelve todos
     *              los labs con nombre. Vacío para otros tipos (el modal
     *              usa otras vistas para images/practices).
     * @returns {Array<{key, label}>}
     */
    orderItems(row) {
      if (!row || row.type !== "lab") return [];
      const labs = Array.isArray(row.labs) ? row.labs : [];
      return labs
        .filter((l) => l && (l.description || l.code))
        .map((l) => ({ key: l._id || l.code, label: l.description || l.code }));
    },
    typeMeta(t) {
      return TYPE_META[t] || { emoji: "•", label: t };
    },

    /**
     * @description Devuelve el status efectivo (default 'new' si el
     *              sub-doc crm no existe todavía).
     */
    statusOf(row) {
      return (row && row.crm && row.crm.status) || "new";
    },
    statusMeta(status) {
      return STATUS_META[status] || { label: status, cls: "is-new" };
    },
    isTerminal(row) {
      const s = this.statusOf(row);
      return s === "appointment-taken" || s === "closed";
    },

    /**
     * @description Reusa los helpers oficiales de patient-utils.js — la
     *              MISMA lógica que el header del PatientDashboard.
     *              Cubre 3 fuentes en orden de prioridad:
     *              1. patient.member (afiliado vinculado)
     *              2. patient.patient.medicalInsuranceObj (nueva estructura)
     *              3. patient.patient.medicalInsurance (estructura antigua)
     *              Devuelve null solo si TODAS las fuentes son N/A.
     */
    coverageOf(user) {
      if (!user) return null;
      const financiador = getMedicalInsuranceName(user);
      const plan = getMedicalInsurancePlan(user);
      const numero = getMedicalInsuranceNumber(user);
      if (financiador === "N/A" && plan === "N/A" && numero === "N/A") {
        return null;
      }
      return { financiador, plan, numero };
    },

    /**
     * @description Compone el historial visible en el modal. Incluye una
     *              entrada sintética "Orden emitida" con `createdAt` +
     *              todos los entries del `crm.history`.
     */
    historyOf(row) {
      const items = [
        {
          at: row.createdAt,
          label: "Orden emitida",
          by: null,
        },
      ];
      if (row.crm && Array.isArray(row.crm.history)) {
        row.crm.history.forEach((h) => {
          items.push({
            at: h.at,
            label: this.statusMeta(h.status).label,
            by: h.by,
            note: h.note,
          });
        });
      }
      return items.sort(
        (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
      );
    },
    historyUser(u) {
      if (!u) return "";
      const parts = [u.firstName, u.lastName].filter(Boolean).join(" ");
      return parts ? ` — ${parts}` : "";
    },
  },
};
</script>

<template lang="pug">
.orders-crm
  .orders-crm__header
    p.orders-crm__hint Órdenes de lab, imagen y prácticas emitidas en el centro. Contactá al paciente para ofrecerle turno.
    button.orders-crm__export(@click="exportCsv" :disabled="exporting")
      micon(name="attach_file")
      span {{ exporting ? "Generando…" : "Exportar CSV" }}

  .orders-crm__counters
    .orders-crm__counter
      strong {{ counts.active }}
      span activas
    .orders-crm__counter-sep ·
    .orders-crm__counter
      strong {{ counts.takenThisWeek }}
      span turno tomado esta semana
    .orders-crm__counter-sep ·
    .orders-crm__counter
      strong {{ counts.closed }}
      span cerradas

  .orders-crm__filters
    input.orders-crm__search(
      type="text"
      v-model="filters.search"
      placeholder="Buscar por paciente / DNI"
    )
    input.orders-crm__date(type="date" v-model="filters.from")
    span.orders-crm__date-sep →
    input.orders-crm__date(type="date" v-model="filters.to")
    select.orders-crm__filter(v-model="filters.type")
      option(value="") Todos los tipos
      option(value="lab") 🔬 Lab
      option(value="images") 🩻 Imagen
      option(value="practices") 📋 Práctica
    select.orders-crm__filter(v-model="filters.status")
      option(value="active") Activas (nueva + contactado + turno propuesto)
      option(value="new") Solo nuevas
      option(value="contacted") Solo contactadas
      option(value="appointment-proposed") Solo turno propuesto
      option(value="appointment-taken") Turno tomado
      option(value="closed") Cerradas
      option(value="") Todas
    select.orders-crm__filter(v-model="filters.doctor")
      option(value="") Todos los prestadores
      option(v-for="d in providers" :key="d._id" :value="d._id") {{ d.firstName }} {{ d.lastName }}
    select.orders-crm__filter(v-model="filters.coverage")
      option(value="") Todas las coberturas
      option(v-for="c in coverages" :key="c.key" :value="c.key") {{ c.label }}
    button.orders-crm__clear(@click="clearFilters") Limpiar

  .orders-crm__empty(v-if="!loading && docs.length === 0")
    p No hay órdenes que coincidan con los filtros.

  table.orders-crm__table(v-if="docs.length > 0")
    thead
      tr
        th Fecha
        th Paciente
        th Cobertura
        th Tipo
        th Prestador
        th Estado
        th
    tbody
      tr(v-for="row in docs" :key="row._id" @click="openDetail(row)")
        td.orders-crm__col-date {{ formatDate(row.createdAt) }}
        td
          strong {{ patientName(row.patient) }}
          .orders-crm__meta(v-if="patientDni(row.patient)") DNI {{ patientDni(row.patient) }}
        td.orders-crm__col-coverage
          template(v-if="coverageOf(row.patient)")
            strong {{ coverageOf(row.patient).financiador }}
            .orders-crm__meta Plan {{ coverageOf(row.patient).plan }}
          span.orders-crm__particular(v-else) Particular
        td.orders-crm__col-type
          .orders-crm__type-inline
            micon.orders-crm__type-icon(:name="typeMeta(row.type).icon")
            span {{ typeMeta(row.type).label }}
          .orders-crm__meta {{ orderShortLabel(row) }}
        td
          strong {{ doctorName(row.doctor) }}
          .orders-crm__meta(v-if="doctorSpecialty(row.doctor)") {{ doctorSpecialty(row.doctor) }}
        td
          span.orders-crm__badge(:class="statusMeta(statusOf(row)).cls") ● {{ statusMeta(statusOf(row)).label }}
        td.orders-crm__col-arrow ›

  //- ═══ Modal detalle ═══
  .orders-crm__modal-backdrop(v-if="detail.visible" @click.self="closeDetail")
    .orders-crm__modal(v-if="detail.indication")
      .orders-crm__modal-header
        h3.orders-crm__modal-title
          micon.orders-crm__type-icon(:name="typeMeta(detail.indication.type).icon")
          span {{ orderTitle(detail.indication) }}
        span.orders-crm__badge(:class="statusMeta(statusOf(detail.indication)).cls")
          | ● {{ statusMeta(statusOf(detail.indication)).label }}

      .orders-crm__section
        h4 Paciente
        .orders-crm__row
          strong {{ patientName(detail.indication.patient) }}
          template(v-if="patientDni(detail.indication.patient)")
            span  · DNI {{ patientDni(detail.indication.patient) }}
        .orders-crm__row.orders-crm__row--contact(v-if="detail.indication.patient && detail.indication.patient.email")
          micon(name="email")
          span {{ detail.indication.patient.email }}
        .orders-crm__row.orders-crm__row--contact(v-if="detail.indication.patient && detail.indication.patient.phone")
          micon(name="call")
          span {{ detail.indication.patient.phone }}
        .orders-crm__row.orders-crm__row--contact.orders-crm__row--warn(v-if="detail.indication.patient && !detail.indication.patient.email && !detail.indication.patient.phone")
          micon(name="info")
          span Sin email ni teléfono cargados

      .orders-crm__section(v-if="coverageOf(detail.indication.patient)")
        h4 Cobertura
        .orders-crm__grid
          .orders-crm__grid-label Financiador:
          .orders-crm__grid-value {{ coverageOf(detail.indication.patient).financiador }}
          .orders-crm__grid-label Plan:
          .orders-crm__grid-value {{ coverageOf(detail.indication.patient).plan }}
          .orders-crm__grid-label Nro afiliado:
          .orders-crm__grid-value {{ coverageOf(detail.indication.patient).numero }}
      .orders-crm__section(v-else)
        h4 Cobertura
        p.orders-crm__particular Particular (sin cobertura registrada)

      .orders-crm__section
        h4 Orden
        .orders-crm__grid
          .orders-crm__grid-label Tipo:
          .orders-crm__grid-value {{ typeMeta(detail.indication.type).label }}
          .orders-crm__grid-label Emitida:
          .orders-crm__grid-value {{ formatDateLong(detail.indication.createdAt) }}
          .orders-crm__grid-label Prestador:
          .orders-crm__grid-value
            strong {{ doctorName(detail.indication.doctor) }}
            .orders-crm__meta(v-if="doctorSpecialty(detail.indication.doctor)")
              | {{ doctorSpecialty(detail.indication.doctor) }}
              template(v-if="doctorLicense(detail.indication.doctor)")
                |  · MN {{ doctorLicense(detail.indication.doctor) }}

      //- Determinaciones de lab como chips (patrón PatientHistory /
      //- evolución médica). Cuando la orden tiene rutina + muchas
      //- determinaciones, es la vista prolija que el operador espera.
      .orders-crm__section(v-if="detail.indication.type === 'lab' && orderItems(detail.indication).length > 0")
        h4 Determinaciones ({{ orderItems(detail.indication).length }})
        .orders-crm__chips
          span.orders-crm__chip(v-for="item in orderItems(detail.indication)" :key="item.key") {{ item.label }}

      //- Descripción libre para prácticas (kine, EEG, etc. — típicamente
      //- el prestador escribe indicaciones adicionales).
      .orders-crm__section(v-else-if="detail.indication.type === 'practices' && detail.indication.description")
        h4 Indicaciones
        p.orders-crm__description {{ detail.indication.description }}

      //- Notas del prestador si están cargadas (observations es opcional
      //- en el schema pero a veces viene con info útil para el operador).
      .orders-crm__section(v-if="detail.indication.observations")
        h4 Observaciones del prestador
        p.orders-crm__description {{ detail.indication.observations }}

      .orders-crm__section(v-if="!isTerminal(detail.indication)")
        h4 Acciones
        .orders-crm__actions
          button.orders-crm__btn.orders-crm__btn--wsp(@click="openWhatsapp" :disabled="detail.saving || !detail.indication.patient || !detail.indication.patient.phone")
            micon(name="call")
            span WhatsApp
          button.orders-crm__btn.orders-crm__btn--secondary(@click="changeStatus('contacted')" :disabled="detail.saving") Marcar contactado
          button.orders-crm__btn.orders-crm__btn--secondary(@click="changeStatus('appointment-proposed')" :disabled="detail.saving") Turno propuesto
          button.orders-crm__btn.orders-crm__btn--primary(@click="changeStatus('appointment-taken')" :disabled="detail.saving") Turno tomado

      .orders-crm__section(v-else)
        p.orders-crm__terminal-note
          | Caso terminado el {{ formatDateLong(detail.indication.crm && detail.indication.crm.updatedAt) }}
          template(v-if="detail.indication.crm && detail.indication.crm.updatedBy")
            |  por {{ historyUser(detail.indication.crm.updatedBy) }}

      .orders-crm__section
        h4 Historial
        ul.orders-crm__history
          li(v-for="(h, idx) in historyOf(detail.indication)" :key="idx")
            span.orders-crm__history-date {{ formatDateLong(h.at) }}
            span  — {{ h.label }}
            span(v-if="h.by") {{ historyUser(h.by) }}
            .orders-crm__history-note(v-if="h.note") "{{ h.note }}"

      .orders-crm__modal-footer
        button.orders-crm__btn.orders-crm__btn--danger(v-if="!isTerminal(detail.indication)" @click="openCloseCase" :disabled="detail.saving") Cerrar caso ✕
        span
        button.orders-crm__btn.orders-crm__btn--secondary(@click="closeDetail") Cerrar

  //- ═══ Confirmación de "Cerrar caso" con nota opcional ═══
  .orders-crm__modal-backdrop(v-if="closeCase.visible" @click.self="cancelCloseCase")
    .orders-crm__modal.orders-crm__modal--small
      h3 Cerrar caso
      p Este caso no va a aparecer más en la bandeja.
      p ¿Anotar el motivo? (opcional — va al historial)
      textarea(
        v-model="closeCase.note"
        rows="3"
        maxlength="500"
        placeholder="No contesta, ya se lo hizo afuera, etc."
        :disabled="closeCase.saving"
      )
      .orders-crm__modal-footer
        button.orders-crm__btn.orders-crm__btn--secondary(@click="cancelCloseCase" :disabled="closeCase.saving") Cancelar
        button.orders-crm__btn.orders-crm__btn--danger(@click="confirmCloseCase" :disabled="closeCase.saving")
          | {{ closeCase.saving ? "Cerrando…" : "Cerrar caso" }}

  //- ═══ Composición de WhatsApp comercial al paciente ═══
  .orders-crm__modal-backdrop(v-if="whatsapp.visible" @click.self="cancelWhatsapp")
    .orders-crm__modal.orders-crm__modal--small
      h3.orders-crm__modal-title
        micon(name="call")
        span WhatsApp al paciente
      p.orders-crm__wsp-hint(v-if="detail.indication && detail.indication.patient")
        | Se envía a
        strong  {{ detail.indication.patient.phone }}
        |  vía el motor de WhatsApp de la organización.
      textarea(
        v-model="whatsapp.message"
        rows="7"
        maxlength="1500"
        placeholder="Escribí el mensaje"
        :disabled="whatsapp.sending"
      )
      p.orders-crm__wsp-hint
        | Al enviar exitoso, el caso pasa a
        strong  Contactado
        |  si estaba
        strong  Nuevo
        | .
      .orders-crm__modal-footer
        button.orders-crm__btn.orders-crm__btn--secondary(@click="cancelWhatsapp" :disabled="whatsapp.sending") Cancelar
        button.orders-crm__btn.orders-crm__btn--wsp(@click="sendWhatsapp" :disabled="whatsapp.sending || !whatsapp.message.trim()")
          | {{ whatsapp.sending ? "Enviando…" : "Enviar WhatsApp" }}
</template>

<style lang="scss" scoped>
.orders-crm {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}
.orders-crm__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.orders-crm__hint {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
  flex: 1;
}
.orders-crm__export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  color: $navy;
  border: 1px solid $navy;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  .micon {
    width: 14px;
    height: 14px;
    fill: $navy;
  }
  &:hover:not(:disabled) {
    background: $navy;
    color: white;
    .micon {
      fill: white;
    }
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.orders-crm__counters {
  display: flex;
  gap: 10px;
  align-items: baseline;
  padding: 10px 14px;
  background: #f8fafc;
  border-left: 3px solid $navy;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #334155;
}
.orders-crm__counter {
  strong {
    color: $navy;
    font-size: 16px;
    margin-right: 4px;
  }
}
.orders-crm__counter-sep {
  color: #cbd5e1;
}
.orders-crm__filters {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
}
.orders-crm__search {
  flex: 1;
  min-width: 220px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  &:focus {
    outline: none;
    border-color: $navy;
  }
}
.orders-crm__date {
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  background: white;
  min-width: 130px;
  color: #334155;
}
.orders-crm__date-sep {
  color: #94a3b8;
  font-size: 14px;
  padding: 0 2px;
}
.orders-crm__filter {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  min-width: 160px;
}
.orders-crm__clear {
  background: none;
  border: none;
  color: $navy;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 8px;
  &:hover {
    text-decoration: underline;
  }
}
.orders-crm__empty {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 13px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
}
.orders-crm__table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  thead th {
    text-align: left;
    padding: 12px;
    color: #64748b;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    &:nth-child(1) {
      width: 95px;
    }
    &:nth-child(2) {
      width: 22%;
    }
    &:nth-child(3) {
      width: 18%;
    }
    &:nth-child(4) {
      width: 24%;
    }
    &:nth-child(5) {
      width: 18%;
    }
    &:nth-child(6) {
      width: 130px;
    }
    &:nth-child(7) {
      width: 28px;
    }
  }
  tbody td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
    overflow: hidden;
    text-overflow: ellipsis;
    strong {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  tbody tr {
    cursor: pointer;
    transition: background 0.12s;
    &:hover {
      background: #f8fafc;
    }
    &:last-child td {
      border-bottom: none;
    }
  }
}
.orders-crm__col-date {
  color: #64748b;
  white-space: nowrap;
  font-size: 12px;
}
.orders-crm__col-coverage {
  color: $navy;
}
.orders-crm__col-type {
  color: $navy;
  font-weight: 600;
}
.orders-crm__type-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  .micon,
  .orders-crm__type-icon {
    width: 16px;
    height: 16px;
    fill: $navy;
    flex-shrink: 0;
  }
}
.orders-crm__col-arrow {
  color: #cbd5e1;
  text-align: right;
  font-size: 18px;
}
.orders-crm__meta {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
  font-weight: 400;
}
.orders-crm__particular {
  color: #94a3b8;
  font-style: italic;
  font-size: 12px;
}
.orders-crm__badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  &.is-new {
    background: rgba(217, 119, 6, 0.12);
    color: #d97706;
  }
  &.is-contacted {
    background: rgba(37, 99, 235, 0.12);
    color: #2563eb;
  }
  &.is-proposed {
    background: rgba(124, 58, 237, 0.12);
    color: #7c3aed;
  }
  &.is-taken {
    background: rgba(22, 163, 74, 0.12);
    color: #16a34a;
  }
  &.is-closed {
    background: #f1f5f9;
    color: #64748b;
  }
}

.orders-crm__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.orders-crm__modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 680px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  &--small {
    max-width: 460px;
  }
  textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    &:focus {
      outline: none;
      border-color: $navy;
    }
  }
}
.orders-crm__modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
  h3 {
    margin: 0;
    font-size: 16px;
    color: $navy;
  }
}
.orders-crm__modal-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  .micon,
  .orders-crm__type-icon {
    width: 20px;
    height: 20px;
    fill: $navy;
    flex-shrink: 0;
  }
}
.orders-crm__row--contact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  .micon {
    width: 14px;
    height: 14px;
    fill: #64748b;
    flex-shrink: 0;
  }
  &.orders-crm__row--warn .micon {
    fill: #d97706;
  }
}
.orders-crm__section {
  margin-bottom: 16px;
  h4 {
    margin: 0 0 8px 0;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    font-weight: 700;
  }
}
.orders-crm__row {
  font-size: 13px;
  color: #334155;
  padding: 3px 0;
  &--warn {
    color: #d97706;
  }
}
.orders-crm__grid {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 4px 12px;
  font-size: 13px;
}
.orders-crm__grid-label {
  color: #64748b;
  font-weight: 700;
}
.orders-crm__grid-value {
  color: #334155;
  strong {
    color: $navy;
  }
}
.orders-crm__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.orders-crm__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
}
.orders-crm__chip {
  display: inline-block;
  padding: 4px 10px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 12px;
  color: $navy;
  line-height: 1.4;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.orders-crm__description {
  margin: 0;
  padding: 10px 12px;
  background: #f8fafc;
  border-left: 3px solid $navy;
  border-radius: 4px;
  font-size: 13px;
  color: #334155;
  white-space: pre-wrap;
  line-height: 1.5;
}
.orders-crm__terminal-note {
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
  padding: 10px 12px;
  border-radius: 6px;
  margin: 0;
}
.orders-crm__history {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  li {
    padding: 5px 0;
    border-top: 1px solid #f1f5f9;
    color: #334155;
    &:first-child {
      border-top: none;
    }
  }
}
.orders-crm__history-date {
  color: #64748b;
  font-family: monospace;
}
.orders-crm__history-note {
  color: #64748b;
  font-style: italic;
  margin-left: 8px;
  margin-top: 2px;
}
.orders-crm__modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}
.orders-crm__btn {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &--primary {
    background: $navy;
    color: white;
    &:hover:not(:disabled) {
      background: darken($navy, 5%);
    }
  }
  &--secondary {
    background: #f1f5f9;
    color: #334155;
    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  }
  &--danger {
    background: #dc2626;
    color: white;
    &:hover:not(:disabled) {
      background: darken(#dc2626, 5%);
    }
  }
  &--wsp {
    background: #25d366;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    .micon {
      width: 16px;
      height: 16px;
      fill: white;
    }
    &:hover:not(:disabled) {
      background: darken(#25d366, 5%);
    }
  }
}
.orders-crm__wsp-hint {
  font-size: 12px;
  color: #64748b;
  margin: 6px 0;
  line-height: 1.4;
}
</style>
